/* eslint-disable */
import React, { useState } from "react";
import type Calc from "../types.d";
import { listify } from "../../../../lib/util/type-helpers";

import { Table, type Column } from "@hdir/ui/table";
import { TextField } from "@hdir/ui/text-field";
import { Button } from "@hdir/ui/button";

import { evaluate } from "mathjs";
import ThemeBox from "/react4xp/shared/ThemeBox";
import Richtext from "/react4xp/shared/Richtext";
import ChevronIcon from "/react4xp/shared/Icons/Chevron";

/*
Description:
This calculator estimates the total number of hours per year spent on various tasks, based on user-defined input values and predefined task tables.
It supports multiple use cases by allowing flexible input definitions and configurable task sets for different roles or occupations.

Usage:
Users define a set of inputs (e.g., number of students, class size, number of units) and configure task lists (via "static value tables") for each relevant role.
Each task includes a calculation formula and an associated constant for a full-time position, enabling the result to be shown both as total hours and as a percentage of a full position.

Inputs:
- No strict requirements on which inputs to use; these are defined by the current setup.
- Example: number of babies born, number of students, class size, group size, number of schools.
- Input field names (techNames) should be meaningful, one-word strings. Use "_" as a separator if needed.

Constants:
- One constant is required per occupation, representing the number of hours in a full-time position for that role.

Static Value Tables:
- At least one static values table is required, with the option to add more (e.g., primary tasks, secondary tasks).
- Each table row represents one task:
  - First column: Task name
  - Second column: Hours per year
  - Each row must define a unique constant name (e.g., nurse1, teacher3)
  - Richtext field serves as a helper/description for the calculation logic

Outputs:
- Total hours per year calculated by summing all row results
- Percentage of a full-time position (based on the constant per occupation)
- Decimal values are shown only for results below 1
*/

// TODO: figure out a good way to let required constants be controlled by editors
const DEFAULT_HOURS_PER_YEAR = 1688;

const flattenValues = (staticInputs: Calc.CalcStaticInputs, values = {}): Record<string, string | number> => {
  /** throws an error to help editors avoid duplicate keys */
  const throwIfDuplicate = (techName: string) => {
    if (techName in values) throw new Error(`techname: "${techName}" is already in use somewhere else!`);
  };

  for (const item of staticInputs) {
    if (!item) continue;

    if (item.constants) {
      listify(item.constants).map((constant) => {
        throwIfDuplicate(constant.techName);

        values[constant.techName] = constant.value;
      });
    }

    if (item.staticValuesTable) {
      listify(item.staticValuesTable).map((table) => {
        listify(table.staticValues).map((item) => {
          throwIfDuplicate(item.techName);

          values[item.techName] = item.value;
        });
      });
    }
  }

  return values;
};

const inputsToState = (inputs: Calc.UserInput | Calc.UserInput[number]) => {
  const inputsArray = Array.isArray(inputs)
    ? inputs
    : [inputs];

  return inputsArray.reduce((valuesObject, { techName, value }) => ({ ...valuesObject, [techName]: value || 0 }), {}) as Record<string, number | string>;
};

type Props = {
  data: Record<string, any>,
  inputs: Calc.UserInput,
  formula: Calc.Formula,
  staticInputs: Calc.CalcStaticInputs
}
/**
 * This requires the contentmodel to be set up in a particular way.
 * TODO: add documentation so that this can be understood by other people
 */
function Entry(props: Props) {
  const inputsStateObj = inputsToState(props.inputs);

  const [values, setValues] = useState(flattenValues(props.staticInputs, inputsStateObj));
  const [tablesState, setTablesState] = useState(props.staticInputs);
  const [showResults, setShowResults] = useState(false);
  const [collapsibles, setCollapsibles] = useState<Record<string, boolean>>(props.staticInputs.reduce((obj, current) => {
    obj[current.tittel] = false;
    return obj;
  }, {}));

  // Hent prosentverdien for merkantil - felles kontorfaglig ressurs (heltall, f.eks. 10 for 10 %) – bruker 10 som standard hvis ikke satt,
  // og gjør om til en desimalfaktor ved å dele på 100.
  const sharePct = Number(props.data.sharedOfficeResourcePercentage) || 10;
  const shareFactor = sharePct / 100;

  const updateCollapsible = (key: string, value: boolean) => {
    setCollapsibles({
      ...collapsibles,
      [key]: value
    });
  };

  const updateValue = (key: string, value: number) => {
    setValues({
      ...values,
      [key]: value
    });
  };
  const getHoursPerYear = (table: Calc.CalcStaticInputs[number]) => listify(table.constants)[0].value || DEFAULT_HOURS_PER_YEAR;
  const minsToHours = (n: number) => {
    // show the first decimal place for small numbers
    if (n < 60) return Math.round(n / 6) / 10;

    return Math.round(n / 60);
  };
  const sum = (n: number[]) => n.reduce((sum, current) => sum += current, 0);
  const staffingPercentage = (hoursPerYearConst: number, hours: number) => Math.round((hours / hoursPerYearConst) * 100);
  const updateTable = () => {
    const newTablesState = tablesState.map((tableGroup) => {
      const sums: number[] = [];

      tableGroup.staticValuesTable = listify((tableGroup.staticValuesTable)).map((table) => {
        const results: number[] = [];

        table.staticValues = listify(table.staticValues).map((staticValue) => {
          staticValue.result = minsToHours(evaluate(staticValue.formula, values));

          results.push(staticValue.result);
          return staticValue;
        });

        table.sum = sum(results);
        sums.push(table.sum);

        return table;
      });

      tableGroup.sum = Math.round(sum(sums));
      return tableGroup;
    });

    setTablesState(newTablesState);
    setShowResults(true);
  };

  const getInputHandler = (key: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!(key in values)) return;
    updateValue(key, parseFloat(e.target.value));
  };

  function KeyValueThemebox({ text, value }: { text: string, value: number | string }) {
    return (
      <ThemeBox noPadding theme="blue-light" size="large">
        <div className="result-box">
          <span className="result-text">{text}</span>
          <span className="result-number">{value}</span>
        </div>
      </ThemeBox>
    );
  }

  return (
    <div className="helsestasjon-calc">
      <div className="b-article-intro__text l-mt-2">
        {props.data.tittel && <h1>{props.data.tittel}</h1>}
        {props.data.intro && <div className="b-article-intro__intro">{props.data.intro}</div>}
        {props.data.tekst && <Richtext className="t-body-text" text={props.data.tekst} />}
      </div>

      <form className="form l-mt-2 l-mb-2">
        <div className="input-container">
          {props.inputs.map((el) => (
            <TextField
              onChange={getInputHandler(el.techName)}
              key={el.techName}
              className="input-field"
              type="number"
              min={0}
              placeholder="0"
              value={values[el.techName] as string}
              label={el.displayName}
              name={el.techName}
            />
          ))}
        </div>

        <Button type="submit" onClick={(e) => { e.preventDefault(); updateTable(); }}>Beregn</Button>
      </form>

      {showResults && (
        <div className="results">
          {tablesState.map((section) => {
            const tables = listify(section.staticValuesTable);
            const hoursPerYear = listify(section.constants)[0]?.value || DEFAULT_HOURS_PER_YEAR;

            return (
              <div className="section">
                <h3 className="section-heading">
                  {section.tittel}
                  <Button
                    onClick={() => updateCollapsible(section.tittel, !collapsibles[section.tittel])}
                    variant="icon"
                    size="small"
                    id={`collapsible-button-${section.tittel}`}
                    aria-controls={`collapsible-region-${section.tittel}`}
                    aria-expanded={collapsibles[section.tittel]}
                  >
                    <ChevronIcon rotate={collapsibles[section.tittel] ? 0 : 180} />
                  </Button>
                </h3>

                <KeyValueThemebox text="Sum (timer)" value={section.sum} />
                <KeyValueThemebox text="Bemanning (stilling)" value={`${staffingPercentage(hoursPerYear, section.sum)}%`} />

                <div id={`collapsible-region-${section.tittel}`} role="region" hidden={!collapsibles[section.tittel]}>
                  <Richtext text={section.tekst} />

                  <div className="tables-container table-reset">
                    {tables.map((table) => {
                      const cols: Column<Calc.StaticValue>[] = [
                        { header: table.firstColumnName, accessorKey: "displayName" },
                        {
                          header: table.secondColumnName,
                          accessorKey: "result",
                          enableResizing: true,
                          size: 140
                        }
                      ];
                      const rows = listify(table.staticValues);

                      return (
                        <Table
                          columns={cols}
                          rows={rows}
                          getRowId={(item) => item.techName}
                          hideFooter
                          isRowExpandable={(item) => !!item.original.text}
                          renderExpandable={({ original }) => (
                            <ThemeBox theme="blue-light" size="large">
                              <Richtext text={original.text} />
                            </ThemeBox>
                          )}
                        />
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}

          <div className="section">
            <h3>Felles kontorfaglig ressurs</h3>
            {props.data.sharedOfficeResource && <Richtext className="t-body-text" text={props.data.sharedOfficeResource} />}

            {(() => {
              const totalPercent = tablesState
                .map(table => staffingPercentage(getHoursPerYear(table), table.sum))
                .reduce((acc, curr) => acc + curr, 0);

              return (
                <KeyValueThemebox
                  text="Felles kontorfaglig ressurs"
                  value={`${Math.round(totalPercent * shareFactor)}%`}
                />
              );
            })()}
          </div>
        </div>
      )}

      {props.data.textUnderCalculator && <Richtext className="t-body-text" text={props.data.textUnderCalculator} />}

    </div>
  );
}

export default function (props) {
  return <Entry {...props} />;
}
